import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X, Clock, MapPin, Users, DollarSign, Flag, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { AvatarStack, PeopleList } from "@/components/app/UserAvatar";
import { sportOf, levelLabel } from "@/lib/games";
import { fmtDate } from "@/lib/adega";
import { reportTarget } from "@/lib/report";

function BringList({ gameId, g, user, canUse }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get(`/games/${gameId}/items`).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, [gameId]); // eslint-disable-line
  const act = async (fn) => { try { await fn(); await load(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="mt-6" data-testid="game-bring-list">
      <div className="text-sm font-semibold mb-2">O que levar</div>
      {items.length === 0 && <p className="text-xs text-muted-foreground">Nenhum item na lista ainda.</p>}
      <ul className="space-y-2">
        {items.map((it) => {
          const mine = it.claimed_by === user?.id;
          return (
            <li key={it.item_id} className="flex items-center gap-2 rounded-lg px-3 py-2.5 border border-white/10 bg-muted/60" data-testid={`game-item-${it.item_id}`}>
              <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{it.name}</div><div className="text-xs text-muted-foreground">{it.claimed_nickname ? `com ${it.claimed_nickname}` : "faltando"}</div></div>
              {canUse && <button onClick={() => act(() => api.post(`/games/${gameId}/items/${it.item_id}/claim`))} disabled={!!it.claimed_by && !mine} className="px-3 h-9 rounded-md bg-primary text-primary-foreground text-xs font-semibold disabled:opacity-40 press" data-testid={`game-claim-${it.item_id}`}>{mine ? "Desmarcar" : "Eu levo"}</button>}
              {g.is_mine && <button onClick={() => act(() => api.delete(`/games/${gameId}/items/${it.item_id}`))} className="h-9 w-9 grid place-items-center text-muted-foreground press"><Trash2 className="w-4 h-4" /></button>}
            </li>
          );
        })}
      </ul>
      {g.is_mine && !g.cancelled && (
        <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) act(async () => { await api.post(`/games/${gameId}/items`, { name }); setName(""); }); }} className="mt-3 flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex: bola, colete, água" data-testid="game-new-item-input" />
          <Button type="submit" className="press" data-testid="game-add-item-button">Adicionar</Button>
        </form>
      )}
    </div>
  );
}

export default function GameSheet({ gameId, onClose, onChanged }) {
  const { user } = useAuth();
  const [g, setG] = useState(null);
  const load = () => api.get(`/games/${gameId}`).then(({ data }) => setG(data)).catch((e) => { toast.error(formatApiError(e)); onClose?.(); });
  useEffect(() => { load(); }, [gameId]); // eslint-disable-line

  const act = async (fn, msg) => {
    if (!user) { toast.error("Entre na sua conta pra participar."); return; }
    try { const r = await fn(); if (r?.data?.id) setG(r.data); else await load(); if (msg) toast.success(msg); onChanged?.(); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!g) return null;
  const sport = sportOf(g.sport);
  const insider = g.is_mine || g.my_status === "in";
  const removePlayer = (p) => window.confirm(`Remover ${p.nickname} do jogo?`) && act(() => api.delete(`/games/${gameId}/players/${p.user_id}`), "Participante removido.");
  const cancel = () => window.confirm("Cancelar este jogo? Os confirmados serão avisados.") && act(() => api.post(`/games/${gameId}/cancel`), "Jogo cancelado.").then(onClose);

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <SheetContent side="bottom" className="max-h-[85dvh] overflow-y-auto rounded-t-3xl bg-card border-t border-white/10 p-0" data-testid="game-sheet">
        <div className="sticky top-0 z-10 flex justify-end px-4 py-2 bg-card/95 backdrop-blur">
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-muted grid place-items-center press" data-testid="close-game-sheet"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 pb-28">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Badge className="bg-emerald-500 text-black">{sport.icon} {sport.label}</Badge>
            <Badge variant="outline" className="border-white/15">{levelLabel(g.level)}</Badge>
            {g.is_live && <Badge className="bg-secondary text-secondary-foreground">Rolando agora</Badge>}
            {g.cancelled && <Badge variant="destructive">Cancelado</Badge>}
          </div>
          <SheetTitle className="text-2xl heading font-extrabold leading-tight" data-testid="game-title">{g.name}</SheetTitle>
          <p className="text-xs text-muted-foreground mt-1">por {g.creator?.nickname}</p>
          {g.description && <p className="mt-3 text-sm text-foreground/90">{g.description}</p>}
          <div className="mt-4 grid gap-2.5 text-sm">
            <div className="flex items-center gap-3"><Clock className="w-4 h-4 text-emerald-400" /> {fmtDate(g.date)} · {g.start_time} – {g.end_time}</div>
            <div className="flex items-center gap-3">
              {g.private ? <Lock className="w-4 h-4 text-emerald-400" /> : <MapPin className="w-4 h-4 text-emerald-400" />}
              {g.lat != null ? <span>{g.address || "Local marcado no mapa"}</span> : <span className="text-muted-foreground italic">Local particular: endereço liberado pra quem entrar</span>}
            </div>
            <div className="flex items-center gap-3" data-testid="game-slots"><Users className="w-4 h-4 text-emerald-400" />
              {g.full ? <b>Completo</b> : <span><b className="text-emerald-400">Faltam {g.remaining}</b> de {g.total_slots} vagas</span>}
              {g.wait_count > 0 && <span className="text-muted-foreground text-xs">· {g.wait_count} na espera</span>}
            </div>
            {g.position && <div className="text-sm text-muted-foreground">Precisa de: <span className="text-foreground">{g.position}</span></div>}
            {g.price && <div className="flex items-center gap-3"><DollarSign className="w-4 h-4 text-emerald-400" /> Rateio {g.price}</div>}
          </div>

          {insider && <div className="mt-4"><AvatarStack people={g.players} total={g.players.length} testid="game-avatars" /></div>}

          {!g.cancelled && !g.is_mine && (
            <div className="mt-5">
              {g.my_status ? (
                <Button variant="outline" className="w-full h-12 press" onClick={() => act(() => api.post(`/games/${gameId}/leave`), g.my_status === "wait" ? "Você saiu da lista de espera." : "Você saiu do jogo.")} data-testid="game-leave-button">
                  {g.my_status === "wait" ? "Sair da lista de espera" : "Sair do jogo"}
                </Button>
              ) : (
                <Button className="w-full h-12 text-base bg-emerald-500 text-black hover:bg-emerald-400 press" onClick={() => act(() => api.post(`/games/${gameId}/join`), g.full ? "Você entrou na lista de espera." : "Você está no jogo!")} data-testid="game-join-button">
                  {g.full ? "Entrar na lista de espera" : "Entrar no jogo"}
                </Button>
              )}
              {g.my_status === "wait" && <p className="text-xs text-muted-foreground mt-2">Você está na lista de espera. Se abrir vaga, entra automaticamente e recebe um aviso.</p>}
            </div>
          )}

          <div className="mt-6">
            <div className="text-sm font-semibold mb-2">Quem vai ({g.in_count})</div>
            {insider ? (
              <>
                {g.players.length === 0 && <p className="text-xs text-muted-foreground">Ninguém entrou ainda.</p>}
                <PeopleList people={g.players} testid="game-players" onRemove={g.is_mine ? removePlayer : null} onReport={(p) => reportTarget("avatar", p.user_id, "esta foto de perfil")} />
                {g.waitlist.length > 0 && <><div className="text-xs font-semibold mt-4 mb-2 text-muted-foreground">Lista de espera</div><PeopleList people={g.waitlist} testid="game-waitlist" onRemove={g.is_mine ? removePlayer : null} /></>}
              </>
            ) : <p className="text-xs text-muted-foreground">{g.in_count} confirmado{g.in_count === 1 ? "" : "s"}. Entre no jogo pra ver quem vai.</p>}
          </div>

          <BringList gameId={gameId} g={g} user={user} canUse={insider && !g.cancelled} />

          <div className="mt-8 flex items-center justify-between">
            {g.is_mine && !g.cancelled ? <Button variant="destructive" onClick={cancel} className="press" data-testid="game-cancel-button">Cancelar jogo</Button> : <span />}
            {!g.is_mine && <button onClick={() => reportTarget("game", gameId, "este jogo")} className="text-xs text-muted-foreground flex items-center gap-1 press" data-testid="report-game-button"><Flag className="w-3.5 h-3.5" /> Denunciar</button>}
          </div>
          <p className="mt-4 text-[11px] text-muted-foreground">Esporte é por conta e risco de cada participante. O organizador é responsável pelo jogo.</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
